import React, { useState, useEffect } from 'react';
import { FaUserCheck, FaUserMinus, FaComment, FaHeart, FaBan, FaFlag, FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, updateDoc, deleteDoc, getDoc, addDoc, increment, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import YapperModal from '../components/YapperModal';
import { Button } from '../components/ds/core/Button';
import { IconButton } from '../components/ds/core/IconButton';
import { Input } from '../components/ds/core/Input';
import { Card } from '../components/ds/core/Card';
import { Avatar } from '../components/ds/core/Avatar';
import { CountryTag } from '../components/globe/CountryTag';
import './FriendsView.css';

function FriendsView({ user }) {
  const [friends, setFriends] = useState([]);
  const [pending, setPending] = useState([]);
  const [tab, setTab] = useState('friends');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'info', onConfirm: null, showCancel: false });
  const navigate = useNavigate();

  const showAlert = (title, message, type = 'info') => {
    setModal({ isOpen: true, title, message, type, onConfirm: () => setModal(m => ({ ...m, isOpen: false })), showCancel: false });
  };

  const showConfirm = (title, message, onConfirmCallback, type = 'warning') => {
    setModal({
      isOpen: true,
      title,
      message,
      type,
      onConfirm: () => {
        setModal(m => ({ ...m, isOpen: false }));
        if (onConfirmCallback) onConfirmCallback();
      },
      showCancel: true
    });
  };

  useEffect(() => {
    loadFriendsData();
  }, [tab]);

  // Listen for report submission from popup
  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data.type === 'SUBMIT_FRIEND_REPORT') {
        try {
          // Double-check if user has already reported this person (race condition prevention)
          const existingReportQuery = query(
            collection(db, 'reports'),
            where('reporterId', '==', event.data.data.reporterId),
            where('reportedId', '==', event.data.data.reportedId)
          );
          const existingReports = await getDocs(existingReportQuery);

          if (!existingReports.empty) {
            return;
          }

          await addDoc(collection(db, 'reports'), {
            reporterId: event.data.data.reporterId,
            reportedId: event.data.data.reportedId,
            reportedUsername: event.data.data.reportedUsername,
            reason: event.data.data.reason,
            timestamp: new Date(),
            status: 'pending'
          });

        } catch (error) {
          console.error('Error submitting report:', error);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const loadFriendsData = async () => {
    setLoading(true);
    try {
      const userId = localStorage.getItem('userId');

      if (tab === 'friends') {
        // Get accepted friendships
        const friendshipsQuery = query(
          collection(db, 'friendships'),
          where('participants', 'array-contains', userId),
          where('status', '==', 'accepted')
        );
        const friendshipsSnapshot = await getDocs(friendshipsQuery);

        const friendsList = [];
        for (const friendDoc of friendshipsSnapshot.docs) {
          const friendData = friendDoc.data();
          const friendId = friendData.participants.find(id => id !== userId);

          // Get friend's user data
          const friendUserDoc = await getDoc(doc(db, 'users', friendId));
          if (friendUserDoc.exists()) {
            friendsList.push({
              id: friendId,
              friendshipDocId: friendDoc.id,
              nickname: friendUserDoc.data().username || 'Unknown User',
              countryCode: friendUserDoc.data().countryCode || null,
              likes: friendUserDoc.data().likes || 0,
            });
          }
        }
        setFriends(friendsList);
      } else {
        // Get pending friend requests (received by current user)
        const pendingQuery = query(
          collection(db, 'friendships'),
          where('participants', 'array-contains', userId),
          where('status', '==', 'pending')
        );
        const pendingSnapshot = await getDocs(pendingQuery);

        const pendingList = [];
        for (const pendingDoc of pendingSnapshot.docs) {
          const pendingData = pendingDoc.data();
          // Only show requests where current user is NOT the requester
          if (pendingData.requesterId !== userId) {
            const requesterId = pendingData.requesterId;

            // Get requester's user data
            const requesterDoc = await getDoc(doc(db, 'users', requesterId));
            if (requesterDoc.exists()) {
              pendingList.push({
                id: requesterId,
                friendshipDocId: pendingDoc.id,
                nickname: requesterDoc.data().username || 'Unknown User',
                countryCode: requesterDoc.data().countryCode || null,
                likes: requesterDoc.data().likes || 0,
              });
            }
          }
        }
        setPending(pendingList);
      }
    } catch (err) {
      console.error('Failed to load friends:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRequest = async (friend) => {
    try {
      const userId = localStorage.getItem('userId');

      // Update friendship status to accepted
      await updateDoc(doc(db, 'friendships', friend.friendshipDocId), {
        status: 'accepted'
      });

      await loadFriendsData();
      showAlert('Friend Added', 'Friend request accepted!', 'success');
    } catch (err) {
      console.error('Failed to accept request:', err);
      showAlert('Error', 'Failed to accept friend request', 'error');
    }
  };

  const handleRejectRequest = async (friend) => {
    try {
      // Delete the friendship request
      await deleteDoc(doc(db, 'friendships', friend.friendshipDocId));
      await loadFriendsData();
    } catch (err) {
      console.error('Failed to reject request:', err);
      showAlert('Error', 'Failed to reject friend request', 'error');
    }
  };

  const handleRemoveFriend = async (friend) => {
    showConfirm('Remove Friend', 'Remove this friend?', async () => {
      try {
        // Delete the friendship
        await deleteDoc(doc(db, 'friendships', friend.friendshipDocId));
        await loadFriendsData();
      } catch (err) {
        console.error('Failed to remove friend:', err);
        showAlert('Error', 'Failed to remove friend', 'error');
      }
    });
  };

  const handleMessage = (friend) => {
    // Navigate to messages and open conversation with this friend
    navigate(`/messages?userId=${friend.id}`);
  };

  const handleLike = async (friend) => {
    try {
      const userId = localStorage.getItem('userId');

      // Check if user already liked this friend
      const existingLikeQuery = query(
        collection(db, 'likes'),
        where('fromUserId', '==', userId),
        where('toUserId', '==', friend.id)
      );
      const existingLikes = await getDocs(existingLikeQuery);

      if (!existingLikes.empty) {
        showAlert('Already Liked', 'You already liked this user!', 'info');
        return;
      }

      // Increment the friend's like count
      await updateDoc(doc(db, 'users', friend.id), {
        likes: increment(1)
      });

      // Track who liked
      await addDoc(collection(db, 'likes'), {
        fromUserId: userId,
        toUserId: friend.id,
        timestamp: serverTimestamp()
      });

      showAlert('Like Sent', `Sent a like to ${friend.nickname}! ❤️`, 'success');
    } catch (err) {
      console.error('Failed to send like:', err);
      showAlert('Error', 'Failed to send like: ' + err.message, 'error');
    }
  };

  const handleBlock = async (friend) => {
    showConfirm('Block User', `Block ${friend.nickname}? This will remove them as a friend and prevent future interactions.`, async () => {
      try {
        const userId = localStorage.getItem('userId');

        // Create block entry
        await addDoc(collection(db, 'blocks'), {
          blockerId: userId,
          blockedId: friend.id,
          timestamp: new Date()
        });

        // Remove friendship
        await deleteDoc(doc(db, 'friendships', friend.friendshipDocId));

        await loadFriendsData();
        showAlert('User Blocked', `${friend.nickname} has been blocked`, 'success');
      } catch (err) {
        console.error('Failed to block user:', err);
        showAlert('Error', 'Failed to block user', 'error');
      }
    });
  };

  const handleReport = async (friend) => {
    const userId = localStorage.getItem('userId');

    // Check if user has already reported this person
    try {
      const existingReportQuery = query(
        collection(db, 'reports'),
        where('reporterId', '==', userId),
        where('reportedId', '==', friend.id)
      );
      const existingReports = await getDocs(existingReportQuery);

      if (!existingReports.empty) {
        showAlert('Already Reported', 'You have already reported this user. Our moderation team is currently reviewing your previous report and will take appropriate action. Thank you for helping keep our community safe.', 'info');
        return;
      }
    } catch (error) {
      console.error('Error checking existing reports:', error);
      showAlert('Error', 'Unable to process your report at this time. Please try again later.', 'error');
      return;
    }

    // Open a new popup window
    const width = 500;
    const height = 600;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    const reportWindow = window.open(
      '',
      'ReportUser',
      `width=${width},height=${height},left=${left},top=${top},resizable=no,scrollbars=yes`
    );

    if (reportWindow) {
      reportWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Report User</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
              padding: 2rem;
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              min-height: 100vh;
            }
            .container {
              background: white;
              padding: 2rem;
              border-radius: 16px;
              box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
              max-width: 450px;
              margin: 0 auto;
            }
            h1 {
              color: #333;
              font-size: 1.75rem;
              margin-bottom: 0.5rem;
              display: flex;
              align-items: center;
              gap: 0.5rem;
            }
            .subtitle {
              color: #666;
              font-size: 0.95rem;
              margin-bottom: 1.5rem;
              line-height: 1.5;
            }
            .report-options {
              display: flex;
              flex-direction: column;
              gap: 0.75rem;
              margin-bottom: 1.5rem;
            }
            .report-option {
              display: flex;
              align-items: center;
              gap: 0.75rem;
              padding: 1rem;
              border: 2px solid #e2e8f0;
              border-radius: 10px;
              cursor: pointer;
              transition: all 0.2s;
              background: white;
            }
            .report-option:hover {
              border-color: #667eea;
              background: rgba(102, 126, 234, 0.05);
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
            }
            .report-option input[type="radio"] {
              width: 20px;
              height: 20px;
              cursor: pointer;
              accent-color: #667eea;
            }
            .report-option label {
              font-size: 1rem;
              color: #333;
              font-weight: 500;
              cursor: pointer;
              flex: 1;
            }
            .other-input {
              margin-top: 1rem;
              padding: 1rem;
              background: #f7fafc;
              border-radius: 10px;
              display: none;
            }
            .other-input.show {
              display: block;
            }
            textarea {
              width: 100%;
              padding: 0.75rem;
              border: 2px solid #e2e8f0;
              border-radius: 8px;
              font-size: 0.95rem;
              font-family: inherit;
              resize: vertical;
              min-height: 100px;
              outline: none;
              transition: all 0.2s;
            }
            textarea:focus {
              border-color: #667eea;
              box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
            }
            .word-count {
              display: block;
              margin-top: 0.5rem;
              color: #666;
              font-size: 0.85rem;
            }
            .buttons {
              display: flex;
              gap: 1rem;
              margin-top: 1.5rem;
            }
            button {
              flex: 1;
              padding: 0.875rem 1.5rem;
              border: none;
              border-radius: 8px;
              font-size: 1rem;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.2s;
            }
            .btn-cancel {
              background: #e2e8f0;
              color: #333;
            }
            .btn-cancel:hover {
              background: #cbd5e0;
            }
            .btn-submit {
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
            }
            .btn-submit:hover {
              transform: translateY(-2px);
              box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
            }
            .btn-submit:disabled {
              opacity: 0.5;
              cursor: not-allowed;
              transform: none;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>🚫 Report User</h1>
            <p class="subtitle">Please select a reason for reporting this user. Your report helps keep Yapp safe for everyone.</p>

            <div class="report-options">
              <div class="report-option">
                <input type="radio" name="reason" value="hate-speech" id="hate-speech">
                <label for="hate-speech">Hate Speech</label>
              </div>

              <div class="report-option">
                <input type="radio" name="reason" value="harassment" id="harassment">
                <label for="harassment">Harassment</label>
              </div>

              <div class="report-option">
                <input type="radio" name="reason" value="spam" id="spam">
                <label for="spam">Spam</label>
              </div>

              <div class="report-option">
                <input type="radio" name="reason" value="inappropriate-content" id="inappropriate">
                <label for="inappropriate">Inappropriate Content</label>
              </div>

              <div class="report-option">
                <input type="radio" name="reason" value="other" id="other">
                <label for="other">Other</label>
              </div>
            </div>

            <div class="other-input" id="otherInput">
              <textarea id="otherText" placeholder="Please describe the reason (max 200 words)"></textarea>
              <span class="word-count" id="wordCount">0 / 200 words</span>
            </div>

            <div class="buttons">
              <button class="btn-cancel" onclick="window.close()">Cancel</button>
              <button class="btn-submit" id="submitBtn">Submit Report</button>
            </div>
          </div>

          <script>
            const otherRadio = document.getElementById('other');
            const otherInput = document.getElementById('otherInput');
            const otherText = document.getElementById('otherText');
            const wordCount = document.getElementById('wordCount');
            const submitBtn = document.getElementById('submitBtn');
            const radios = document.querySelectorAll('input[name="reason"]');

            // Show/hide other input
            radios.forEach(radio => {
              radio.addEventListener('change', () => {
                if (radio.value === 'other') {
                  otherInput.classList.add('show');
                } else {
                  otherInput.classList.remove('show');
                }
              });
            });

            // Word counter
            otherText.addEventListener('input', (e) => {
              const words = e.target.value.trim().split(/\\s+/).filter(w => w.length > 0);
              const count = words.length;
              wordCount.textContent = count + ' / 200 words';

              if (count > 200) {
                const limited = words.slice(0, 200).join(' ');
                e.target.value = limited;
                wordCount.textContent = '200 / 200 words';
              }
            });

            // Submit report
            submitBtn.addEventListener('click', async () => {
              const selectedReason = document.querySelector('input[name="reason"]:checked');

              if (!selectedReason) {
                alert('Please select a reason for reporting');
                return;
              }

              if (selectedReason.value === 'other' && !otherText.value.trim()) {
                alert('Please describe the reason for reporting');
                return;
              }

              const reason = selectedReason.value === 'other' ? otherText.value.trim() : selectedReason.value;

              submitBtn.disabled = true;
              submitBtn.textContent = 'Submitting...';

              try {
                // Send report data to parent window
                window.opener.postMessage({
                  type: 'SUBMIT_FRIEND_REPORT',
                  data: {
                    reporterId: ${JSON.stringify(userId)},
                    reportedId: ${JSON.stringify(friend.id)},
                    reportedUsername: ${JSON.stringify(friend.nickname).replace(/</g, '\\u003c')},
                    reason: reason
                  }
                }, window.location.origin);

                alert('User reported. Thank you for keeping Yapp safe.');
                window.close();
              } catch (error) {
                alert('Failed to submit report. Please try again.');
                submitBtn.disabled = false;
                submitBtn.textContent = 'Submit Report';
              }
            });
          </script>
        </body>
        </html>
      `);
      reportWindow.document.close();
    }
  };

  return (
    <div className="friends-view">
      <div className="friends-container">
        <div className="friends-header">
          <h1>Friends</h1>
          <div className="friends-controls">
            <div className="search-bar">
              <Input
                type="text"
                placeholder="Search friends..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                icon={<FaSearch />}
                fullWidth
                style={{
                  borderRadius: 'var(--radius-pill)',
                  background: '#fff',
                  border: '1px solid var(--border-strong)',
                  color: 'var(--text-strong)',
                }}
              />
            </div>
            <div className="tab-buttons">
              <button
                className={`tab-btn ${tab === 'friends' ? 'active' : ''}`}
                onClick={() => setTab('friends')}
              >
                My Friends ({friends.length})
              </button>
              <button
                className={`tab-btn ${tab === 'pending' ? 'active' : ''}`}
                onClick={() => setTab('pending')}
              >
                Pending ({pending.length})
              </button>
            </div>
          </div>
        </div>

        <div className="friends-grid">
          {loading ? (
            <p className="loading">Loading...</p>
          ) : tab === 'friends' && friends.length === 0 ? (
            <p className="empty">No friends yet. Exchange messages to connect!</p>
          ) : tab === 'pending' && pending.length === 0 ? (
            <p className="empty">No pending requests</p>
          ) : (
            (tab === 'friends' ? friends : pending)
              .filter(friend => friend.nickname?.toLowerCase().includes(searchQuery.toLowerCase()))
              .map((friend) => (
              <Card
                key={friend.id}
                variant="glass"
                padding="md"
                hover
                className="friend-card"
                style={{ background: '#e6e7eb', color: 'var(--text-body)', border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-md)', backdropFilter: 'none' }}
              >
                <Avatar
                  name={friend.nickname}
                  kind={tab === 'pending' ? 'brand' : 'friend'}
                  size={56}
                  className="friend-avatar"
                />
                <h3>{friend.nickname}</h3>
                {friend.countryCode && (
                  <CountryTag iso={friend.countryCode} name={friend.countryCode} tone="soft" size="sm" pin />
                )}
                <div className="friend-actions">
                  {tab === 'pending' ? (
                    <div className="friend-actions-row">
                      <Button
                        variant="primary"
                        size="sm"
                        icon={<FaUserCheck />}
                        onClick={() => handleAcceptRequest(friend)}
                        title="Accept"
                      >
                        Accept
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRejectRequest(friend)}
                        title="Reject"
                      >
                        Reject
                      </Button>
                    </div>
                  ) : (
                    <div className="friend-actions-row">
                      <Button
                        variant="primary"
                        size="sm"
                        icon={<FaComment />}
                        onClick={() => handleMessage(friend)}
                        title="Message"
                      >
                        Message
                      </Button>
                      <IconButton
                        variant="soft"
                        size="sm"
                        label="Send Like"
                        onClick={() => handleLike(friend)}
                      >
                        <FaHeart />
                      </IconButton>
                      <IconButton
                        variant="soft"
                        size="sm"
                        label="Remove Friend"
                        onClick={() => handleRemoveFriend(friend)}
                      >
                        <FaUserMinus />
                      </IconButton>
                      <IconButton
                        variant="danger"
                        size="sm"
                        label="Block User"
                        onClick={() => handleBlock(friend)}
                      >
                        <FaBan />
                      </IconButton>
                      <IconButton
                        variant="danger"
                        size="sm"
                        label="Report User"
                        onClick={() => handleReport(friend)}
                      >
                        <FaFlag />
                      </IconButton>
                    </div>
                  )}
                </div>
                {tab === 'friends' && (
                  <div className="friend-likes">
                    <FaHeart /> {friend.likes} likes
                  </div>
                )}
              </Card>
            ))
          )}
        </div>
      </div>

      <YapperModal
        isOpen={modal.isOpen}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onConfirm={modal.onConfirm}
        onCancel={modal.showCancel ? () => setModal(m => ({ ...m, isOpen: false })) : null}
      />
    </div>
  );
}

export default FriendsView;
